import { useNavigate } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, BookOpen } from "lucide-react";
import { t, deptText, payText } from "./i18n";
import { paymentDirectUrl, paymentPortalUrl } from "./deptHelpers";
import {
  allPaymentEntries,
  getPaymentBySlug,
  getCityBySlug,
  cityPayments,
  guideTitle,
  guideDescription,
  guideSteps,
  guideFaqs,
} from "./seoPages";
import { ShareBar } from "./share";

function GuideBack({ lang, onBack }) {
  return (
    <button type="button" className="if-legal-back" onClick={onBack}>
      ← {t(lang, "home")}
    </button>
  );
}

export function GuidesIndexPage({ lang, onOpenGuide }) {
  const entries = allPaymentEntries();
  const navigate = useNavigate();

  return (
    <article className="if-guide if-guide-index">
      <GuideBack lang={lang} onBack={() => navigate("/")} />
      <header className="if-guide-head">
        <BookOpen size={28} aria-hidden="true" />
        <h1>{t(lang, "guidesTitle")}</h1>
        <p>{t(lang, "guidesSub", entries.length)}</p>
      </header>
      <ul className="if-guide-list">
        {entries.map((entry) => {
          const pt = payText(entry.payment, entry.dept.id, entry.index, lang);
          return (
            <li key={entry.slug}>
              <button type="button" className="if-guide-list-item" onClick={() => onOpenGuide(entry.slug)}>
                <span className="if-guide-list-title">{pt.name}</span>
                <span className="if-guide-list-meta">{entry.payment.portal}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </article>
  );
}

export function PaymentGuidePage({ lang, slug, onBack }) {
  const entry = getPaymentBySlug(slug);
  const navigate = useNavigate();

  if (!entry) {
    navigate("/", { replace: true });
    return null;
  }

  const pt = payText(entry.payment, entry.dept.id, entry.index, lang);
  const dt = deptText(entry.dept, lang);
  const title = guideTitle(entry, lang);
  const steps = guideSteps(entry, lang);
  const faqs = guideFaqs(entry, lang);

  return (
    <article className="if-guide">
      <GuideBack lang={lang} onBack={onBack || (() => navigate("/guides"))} />
      <header className="if-guide-head">
        <p className="if-guide-tag">{dt.dept}</p>
        <h1>{lang === "ta" ? guideTitle(entry, "ta") : title}</h1>
        <p className="if-guide-desc">{pt.desc}</p>
      </header>

      <section className="if-guide-block">
        <h2>{t(lang, "guideStepsTitle")}</h2>
        <ol className="if-guide-steps">
          {steps.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      </section>

      <section className="if-guide-block if-guide-official">
        <h2>{t(lang, "guideOfficialTitle")}</h2>
        <p>{t(lang, "guideOfficialBody")}</p>
        <a
          className="if-guide-portal-btn"
          href={entry.payment.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <ShieldCheck size={16} aria-hidden="true" />
          {entry.payment.portal}
          <ArrowUpRight size={16} aria-hidden="true" />
        </a>
        <p className="if-guide-url">
          <code>{entry.payment.url}</code>
        </p>
      </section>

      <section className="if-guide-block">
        <h2>{t(lang, "guideFaqTitle")}</h2>
        <dl className="if-guide-faq">
          {faqs.map((f) => (
            <div key={f.q}>
              <dt>{f.q}</dt>
              <dd>{f.a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <p className="if-guide-note">{t(lang, "guideDisclaimer")}</p>
      <ShareBar
        lang={lang}
        path={`/pay/${slug}`}
        title={title}
        messageKey="shareMsgGuide"
        msgName={pt.name}
      />
    </article>
  );
}

export function CityHubPage({ lang, slug, onOpenDept }) {
  const city = getCityBySlug(slug);
  const navigate = useNavigate();

  if (!city) {
    navigate("/", { replace: true });
    return null;
  }

  const payments = cityPayments(city);
  const name = lang === "ta" ? city.nameTa : city.name;
  const desc = lang === "ta" ? city.descTa : city.desc;

  return (
    <article className="if-guide if-city">
      <GuideBack lang={lang} onBack={() => navigate("/")} />
      <header className="if-guide-head">
        <h1>{t(lang, "cityTitle", name)}</h1>
        <p>{desc}</p>
        <p className="if-guide-count">{t(lang, "paymentLinks", payments.length)}</p>
      </header>

      <ul className="if-guide-list">
        {payments.map((entry) => {
          const pt = payText(entry.payment, entry.dept.id, entry.index, lang);
          const dt = deptText(entry.dept, lang);
          return (
            <li key={entry.slug} className="if-city-item">
              <div className="if-city-item-main">
                <strong>{pt.name}</strong>
                <span>{dt.short} · {entry.payment.portal}</span>
              </div>
              <div className="if-city-item-actions">
                <a
                  className="if-guide-link-btn"
                  href={paymentDirectUrl(entry.payment)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t(lang, "guideLink")}
                </a>
                <a
                  className="if-guide-link-btn if-guide-link-btn--primary"
                  href={paymentPortalUrl(entry.payment)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t(lang, "openPortal")}
                </a>
              </div>
            </li>
          );
        })}
      </ul>
      <ShareBar
        lang={lang}
        path={`/city/${slug}`}
        title={t(lang, "cityTitle", name)}
        messageKey="shareMsgDept"
        msgName={name}
      />
    </article>
  );
}

export function getGuideSeo(slug, lang = "en") {
  const entry = getPaymentBySlug(slug);
  if (!entry) return null;
  return {
    title: guideTitle(entry, lang),
    description: guideDescription(entry, lang),
    path: `/pay/${slug}`,
  };
}

export function getCitySeo(slug, lang = "en") {
  const city = getCityBySlug(slug);
  if (!city) return null;
  const name = lang === "ta" ? city.nameTa : city.name;
  const desc = lang === "ta" ? city.descTa : city.desc;
  return {
    title: lang === "ta" ? `${name} அரசு கட்டண இணைப்புகள்` : `${name} government payment links`,
    description: desc,
    path: `/city/${slug}`,
  };
}
